import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authenticatedFetch } from "@/libs/api";
import {
  Card,
  Button,
  Tag,
  Space,
  Typography,
  message,
  Spin,
  Table,
  Statistic,
  Row,
  Col,
  Modal,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Popconfirm,
  Alert,
  Divider,
  Empty,
} from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  ArrowLeftOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  ComposedChart,
  Line,
  Bar,
  Area,
  AreaChart,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import PageLayout from "../pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

const COLORS = {
  pv: "#faad14",
  load: "#1890ff",
  pbat: "#52c41a",
  soc: "#2f54eb",
  price: "#eb2f96",
  revenue: "#13c2c2",
};

const STATUS_TAG = {
  pending: { color: "default", icon: <ClockCircleOutlined /> },
  loading: { color: "processing", icon: <SyncOutlined spin /> },
  running: { color: "processing", icon: <SyncOutlined spin /> },
  completed: { color: "success", icon: <CheckCircleOutlined /> },
  failed: { color: "error", icon: <CloseCircleOutlined /> },
};

const fetchApi = async (url, options = {}) => {
  const res = await authenticatedFetch(`${API_BASE}${url}`, {
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.json();
};

// ---------------------------------------------------------------------------
// StatusTag
// ---------------------------------------------------------------------------
function StatusTag({ status }) {
  const cfg = STATUS_TAG[status] || STATUS_TAG.pending;
  return (
    <Tag color={cfg.color} icon={cfg.icon}>
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : "Unknown"}
    </Tag>
  );
}

// ---------------------------------------------------------------------------
// CreateBacktestModal
// ---------------------------------------------------------------------------
function CreateBacktestModal({ open, onClose, onCreated }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPoliciesLoading(true);
    fetchApi("/api/policies")
      .then((data) => {
        const list = Array.isArray(data) ? data : data.policies || [];
        setPolicies(list);
      })
      .catch(() => setPolicies([]))
      .finally(() => setPoliciesLoading(false));

    form.setFieldsValue({
      name: `Backtest ${dayjs().format("YYYY-MM-DD")}`,
      initialSoc: 50,
      stepSize: 5,
    });
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        name: values.name,
        systemSns: values.systemSns,
        startDate: values.dateRange[0].toISOString(),
        endDate: values.dateRange[1].toISOString(),
        policyIds: values.policyIds,
        initialSoc: values.initialSoc,
        stepSize: values.stepSize,
      };
      const result = await fetchApi("/api/backtest/jobs", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      message.success("Backtest job created");
      form.resetFields();
      onCreated(result);
    } catch (err) {
      if (err.errorFields) return; // validation error
      message.error(err.message || "Failed to create backtest job");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Create Backtest"
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={submitting}
      okText="Create"
      destroyOnClose
      width={560}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please enter a name" }]}
        >
          <Input placeholder="Backtest YYYY-MM-DD" />
        </Form.Item>

        <Form.Item
          name="systemSns"
          label="System Serial Numbers"
          rules={[{ required: true, message: "Enter at least one system SN" }]}
        >
          <Select
            mode="tags"
            placeholder="Enter system serial numbers"
            tokenSeparators={[","]}
          />
        </Form.Item>

        <Form.Item
          name="dateRange"
          label="Date Range"
          rules={[{ required: true, message: "Select a date range" }]}
        >
          <RangePicker style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          name="policyIds"
          label="Policies"
          rules={[{ required: true, message: "Select at least one policy" }]}
        >
          <Select
            mode="multiple"
            placeholder="Select policies"
            loading={policiesLoading}
            options={policies.map((p) => ({
              label: p.name || p.id,
              value: p.id,
            }))}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="initialSoc"
              label="Initial SOC %"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} max={100} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="stepSize"
              label="Step Size"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { label: "5 min", value: 5 },
                  { label: "15 min", value: 15 },
                  { label: "30 min", value: 30 },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// JobList
// ---------------------------------------------------------------------------
function JobList() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [deleting, setDeleting] = useState({});

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchApi("/api/backtest/jobs");
      setJobs(Array.isArray(data) ? data : data.jobs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleDelete = async (jobId) => {
    try {
      setDeleting((prev) => ({ ...prev, [jobId]: true }));
      await fetchApi(`/api/backtest/jobs/${jobId}`, { method: "DELETE" });
      message.success("Backtest job deleted");
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch (err) {
      message.error(err.message || "Failed to delete job");
    } finally {
      setDeleting((prev) => ({ ...prev, [jobId]: false }));
    }
  };

  const handleCreated = (result) => {
    setCreateOpen(false);
    if (result && result.id) {
      navigate(`/core-services/backtest/${result.id}`);
    } else {
      fetchJobs();
    }
  };

  // Stats
  const total = jobs.length;
  const running = jobs.filter(
    (j) => j.status === "running" || j.status === "loading"
  ).length;
  const completed = jobs.filter((j) => j.status === "completed").length;
  const failed = jobs.filter((j) => j.status === "failed").length;

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Systems",
      dataIndex: "systemSns",
      key: "systems",
      render: (sns) => (Array.isArray(sns) ? sns.length : sns || 0),
    },
    {
      title: "Policies",
      dataIndex: "policyIds",
      key: "policies",
      render: (ids) => (Array.isArray(ids) ? ids.length : ids || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "—"),
      sorter: (a, b) =>
        new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      defaultSortOrder: "descend",
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/core-services/backtest/${record.id}`);
            }}
          >
            View
          </Button>
          <Popconfirm
            title="Delete this backtest job?"
            onConfirm={(e) => {
              e.stopPropagation();
              handleDelete(record.id);
            }}
            onCancel={(e) => e.stopPropagation()}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deleting[record.id]}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message="Failed to load backtest jobs"
        description={error}
        showIcon
        action={
          <Button size="small" onClick={fetchJobs}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <>
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false}>
            <Statistic title="Total" value={total} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false}>
            <Statistic
              title="Running"
              value={running}
              valueStyle={{ color: "#1890ff" }}
              prefix={<SyncOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false}>
            <Statistic
              title="Completed"
              value={completed}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false}>
            <Statistic
              title="Failed"
              value={failed}
              valueStyle={{ color: "#ff4d4f" }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Button icon={<ReloadOutlined />} onClick={fetchJobs}>
          Refresh
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
        >
          Create Backtest
        </Button>
      </div>

      {/* Table */}
      <Card bordered={false}>
        <Table
          dataSource={jobs}
          columns={columns}
          rowKey="id"
          pagination={makePagination(pageSize, setPageSize)}
          onRow={(record) => ({
            onClick: () => navigate(`/core-services/backtest/${record.id}`),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <Empty
                image={<ExperimentOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />}
                description="No backtest jobs yet"
              >
                <Button type="primary" onClick={() => setCreateOpen(true)}>
                  Create your first backtest
                </Button>
              </Empty>
            ),
          }}
        />
      </Card>

      <CreateBacktestModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// JobDetail
// ---------------------------------------------------------------------------
function JobDetail({ jobId }) {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [timeseriesLoading, setTimeseriesLoading] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);

  // ------ fetch job ------
  const fetchJob = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchApi(`/api/backtest/jobs/${jobId}`);
      setJob(data);

      // Auto-select first run if none selected
      if (!selectedRunId && data.runs && data.runs.length > 0) {
        setSelectedRunId(data.runs[0].id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId, selectedRunId]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  // ------ polling for in-progress jobs ------
  useEffect(() => {
    if (!job || job.status === "completed" || job.status === "failed") return;
    const interval = setInterval(() => fetchJob(), 3000);
    return () => clearInterval(interval);
  }, [job?.status, fetchJob]);

  // ------ fetch timeseries for selected run ------
  useEffect(() => {
    if (!selectedRunId || !job || job.status !== "completed") return;
    let cancelled = false;
    setTimeseriesLoading(true);
    fetchApi(`/api/backtest/runs/${selectedRunId}/timeseries`)
      .then((data) => {
        if (!cancelled) setTimeseries(data);
      })
      .catch(() => {
        if (!cancelled) setTimeseries(null);
      })
      .finally(() => {
        if (!cancelled) setTimeseriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRunId, jobId, job?.status]);

  // ------ helpers ------
  const runs = job?.runs || [];
  const completedRuns = runs.filter((r) => r.status === "completed").length;
  const totalRuns = runs.length;
  const summary = job?.summary || {};

  const formatCurrency = (val) => {
    if (val == null) return "—";
    return `$${Number(val).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const deltaColor = (val) => {
    if (val == null) return undefined;
    return val >= 0 ? "#52c41a" : "#ff4d4f";
  };

  const tsData = Array.isArray(timeseries)
    ? timeseries
    : timeseries?.data || [];

  // ------ run options for chart selector ------
  const runOptions = runs.map((r) => ({
    label: `${r.systemSn || r.system || r.id?.slice(0, 8)} - ${r.policyName || r.policySource || "policy"}`,
    value: r.id,
  }));

  // ------ system comparison table columns ------
  const comparisonColumns = [
    {
      title: "System",
      dataIndex: "systemSn",
      key: "systemSn",
      render: (val) => <Text strong>{val}</Text>,
    },
    {
      title: "BL Revenue",
      dataIndex: "baselineRevenue",
      key: "baselineRevenue",
      render: formatCurrency,
      align: "right",
    },
    {
      title: "AI Revenue",
      dataIndex: "aiRevenue",
      key: "aiRevenue",
      render: formatCurrency,
      align: "right",
    },
    {
      title: "Delta $",
      dataIndex: "revenueDelta",
      key: "revenueDelta",
      render: (val) => (
        <Text style={{ color: deltaColor(val) }}>{formatCurrency(val)}</Text>
      ),
      align: "right",
    },
    {
      title: "Delta %",
      dataIndex: "revenueDeltaPct",
      key: "revenueDeltaPct",
      render: (val) =>
        val != null ? (
          <Text style={{ color: deltaColor(val) }}>
            {val >= 0 ? "+" : ""}
            {val.toFixed(2)}%
          </Text>
        ) : (
          "—"
        ),
      align: "right",
    },
    {
      title: "Cycles",
      dataIndex: "cycles",
      key: "cycles",
      align: "right",
      render: (val) => (val != null ? val : "—"),
    },
    {
      title: "Violations",
      dataIndex: "violations",
      key: "violations",
      align: "right",
      render: (val) =>
        val != null ? (
          <Text type={val > 0 ? "danger" : undefined}>{val}</Text>
        ) : (
          "—"
        ),
    },
  ];

  // ------ run table columns ------
  const runColumns = [
    {
      title: "Run ID",
      dataIndex: "id",
      key: "id",
      render: (val) => (
        <Text copyable={{ text: val }} style={{ fontFamily: "monospace" }}>
          {val ? val.slice(0, 8) + "..." : "—"}
        </Text>
      ),
    },
    {
      title: "System",
      dataIndex: "systemSn",
      key: "systemSn",
    },
    {
      title: "Policy Source",
      dataIndex: "policySource",
      key: "policySource",
      render: (val) => val || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: "Metrics",
      key: "metrics",
      render: (_, record) => {
        const m = record.metrics;
        if (!m) return "—";
        return (
          <Space size="small" split={<Divider type="vertical" />}>
            {m.revenue != null && (
              <Text type="secondary">Rev: {formatCurrency(m.revenue)}</Text>
            )}
            {m.cycles != null && (
              <Text type="secondary">Cycles: {m.cycles}</Text>
            )}
          </Space>
        );
      },
    },
  ];

  // ------ XAxis tick formatter ------
  const formatXTick = (val) => {
    if (!val) return "";
    return dayjs(val).format("HH:mm");
  };

  const formatXTickDate = (val) => {
    if (!val) return "";
    return dayjs(val).format("MM/DD HH:mm");
  };

  // Decide tick formatter based on data span
  const tickFormatter =
    tsData.length > 0
      ? (() => {
          const first = dayjs(tsData[0]?.timestamp || tsData[0]?.time);
          const last = dayjs(
            tsData[tsData.length - 1]?.timestamp ||
              tsData[tsData.length - 1]?.time
          );
          return last.diff(first, "hour") > 24 ? formatXTickDate : formatXTick;
        })()
      : formatXTick;

  // ------ Loading / Error states ------
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message="Failed to load backtest job"
        description={error}
        showIcon
        action={
          <Space>
            <Button size="small" onClick={fetchJob}>
              Retry
            </Button>
            <Button
              size="small"
              onClick={() => navigate("/core-services/backtest")}
            >
              Back to list
            </Button>
          </Space>
        }
      />
    );
  }

  if (!job) {
    return (
      <Empty description="Job not found">
        <Button onClick={() => navigate("/core-services/backtest")}>
          Back to list
        </Button>
      </Empty>
    );
  }

  const isRunning =
    job.status === "running" ||
    job.status === "loading" ||
    job.status === "pending";
  const isCompleted = job.status === "completed";

  return (
    <>
      {/* Back button */}
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/core-services/backtest")}
        style={{ marginBottom: 16 }}
      >
        Back to Backtest Jobs
      </Button>

      {/* KPI row */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false}>
            <Statistic
              title="Status"
              valueRender={() => <StatusTag status={job.status} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false}>
            <Statistic
              title="Progress"
              value={`${completedRuns} / ${totalRuns}`}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false}>
            <Statistic
              title="Step Size"
              value={job.stepSize ? `${job.stepSize} min` : "—"}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false}>
            <Statistic
              title="Policies"
              value={
                Array.isArray(job.policyIds) ? job.policyIds.length : "—"
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Running alert */}
      {isRunning && (
        <Alert
          type="info"
          showIcon
          icon={<SyncOutlined spin />}
          message="Backtest is running"
          description={`Completed ${completedRuns} of ${totalRuns} simulation runs. This page refreshes automatically.`}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Failed alert */}
      {job.status === "failed" && (
        <Alert
          type="error"
          showIcon
          message="Backtest failed"
          description={job.error || "An error occurred during the backtest."}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Portfolio Summary */}
      {isCompleted && summary && (
        <Card
          title="Portfolio Summary"
          bordered={false}
          style={{ marginBottom: 16 }}
        >
          <Row gutter={24}>
            <Col xs={24} sm={8}>
              <Statistic
                title="Baseline Revenue"
                value={summary.baselineRevenue}
                precision={2}
                prefix="$"
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="AI Revenue"
                value={summary.aiRevenue}
                precision={2}
                prefix="$"
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Revenue Delta"
                value={summary.revenueDelta}
                precision={2}
                prefix="$"
                valueStyle={{ color: deltaColor(summary.revenueDelta) }}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* System Comparison */}
      {isCompleted && Array.isArray(summary.systems) && (
        <Card
          title="System Comparison"
          bordered={false}
          style={{ marginBottom: 16 }}
        >
          <Table
            dataSource={summary.systems}
            columns={comparisonColumns}
            rowKey="systemSn"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* Simulation Charts */}
      {isCompleted && (
        <Card
          title="Simulation Charts"
          bordered={false}
          style={{ marginBottom: 16 }}
          extra={
            <Select
              value={selectedRunId}
              onChange={setSelectedRunId}
              options={runOptions}
              style={{ width: 280 }}
              placeholder="Select a run"
            />
          }
        >
          {timeseriesLoading ? (
            <div style={{ textAlign: "center", padding: 60 }}>
              <Spin />
            </div>
          ) : tsData.length === 0 ? (
            <Empty description="No timeseries data available for this run" />
          ) : (
            <>
              {/* Chart 1: Dispatch + PV + Load + Price */}
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Dispatch, PV, Load &amp; Price
              </Text>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart
                  data={tsData}
                  margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                  syncId="backtest"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={tickFormatter}
                    minTickGap={40}
                    fontSize={11}
                  />
                  <YAxis
                    yAxisId="kw"
                    label={{
                      value: "kW",
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 11 },
                    }}
                    fontSize={11}
                  />
                  <YAxis
                    yAxisId="price"
                    orientation="right"
                    label={{
                      value: "$/MWh",
                      angle: 90,
                      position: "insideRight",
                      style: { fontSize: 11 },
                    }}
                    fontSize={11}
                  />
                  <RechartsTooltip
                    labelFormatter={(val) =>
                      dayjs(val).format("YYYY-MM-DD HH:mm")
                    }
                  />
                  <Legend />
                  <Bar
                    yAxisId="kw"
                    dataKey="pbat_kw"
                    name="Battery (kW)"
                    fill={COLORS.pbat}
                    opacity={0.7}
                  />
                  <Line
                    yAxisId="kw"
                    dataKey="pv_kw"
                    name="PV (kW)"
                    stroke={COLORS.pv}
                    dot={false}
                    strokeWidth={1.5}
                  />
                  <Line
                    yAxisId="kw"
                    dataKey="load_kw"
                    name="Load (kW)"
                    stroke={COLORS.load}
                    dot={false}
                    strokeWidth={1.5}
                  />
                  <Line
                    yAxisId="price"
                    dataKey="price"
                    name="Price ($/MWh)"
                    stroke={COLORS.price}
                    dot={false}
                    strokeWidth={1.5}
                    strokeDasharray="4 2"
                  />
                </ComposedChart>
              </ResponsiveContainer>

              <Divider style={{ margin: "12px 0" }} />

              {/* Chart 2: SOC */}
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                State of Charge
              </Text>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart
                  data={tsData}
                  margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                  syncId="backtest"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={tickFormatter}
                    minTickGap={40}
                    fontSize={11}
                  />
                  <YAxis
                    domain={[0, 100]}
                    label={{
                      value: "SOC %",
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 11 },
                    }}
                    fontSize={11}
                  />
                  <RechartsTooltip
                    labelFormatter={(val) =>
                      dayjs(val).format("YYYY-MM-DD HH:mm")
                    }
                  />
                  <Legend />
                  <Area
                    dataKey="soc_pct"
                    name="SOC %"
                    stroke={COLORS.soc}
                    fill={COLORS.soc}
                    fillOpacity={0.15}
                    strokeWidth={1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>

              <Divider style={{ margin: "12px 0" }} />

              {/* Chart 3: Revenue per interval */}
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Revenue per Interval
              </Text>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  data={tsData}
                  margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                  syncId="backtest"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={tickFormatter}
                    minTickGap={40}
                    fontSize={11}
                  />
                  <YAxis
                    label={{
                      value: "$",
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 11 },
                    }}
                    fontSize={11}
                  />
                  <RechartsTooltip
                    labelFormatter={(val) =>
                      dayjs(val).format("YYYY-MM-DD HH:mm")
                    }
                  />
                  <Legend />
                  <Bar
                    dataKey="revenue"
                    name="Revenue ($)"
                    fill={COLORS.revenue}
                    opacity={0.8}
                  />
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </Card>
      )}

      {/* Runs table */}
      <Card
        title="Runs"
        bordered={false}
        style={{ marginBottom: 16 }}
        extra={
          <Text type="secondary">
            {completedRuns} / {totalRuns} completed
          </Text>
        }
      >
        {runs.length === 0 ? (
          <Empty description="No runs recorded yet" />
        ) : (
          <Table
            dataSource={runs}
            columns={runColumns}
            rowKey="id"
            pagination={runs.length > 20 ? { pageSize: 20 } : false}
            size="small"
          />
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Backtest Component
// ---------------------------------------------------------------------------
export default function Backtest() {
  const { jobId } = useParams();

  if (jobId) {
    return (
      <PageLayout active="backtest">
        <JobDetail jobId={jobId} />
      </PageLayout>
    );
  }

  return (
    <PageLayout active="backtest">
      <JobList />
    </PageLayout>
  );
}
